import { useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';

interface Status {
  connected: boolean;
  emailAddress: string | null;
  connectedAt: string | null;
  oauthClientConfigured: boolean;
  requiredScopes: string[];
  callbackUrl: string;
}

export function MyTeamsLicensePanel(): React.ReactElement {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<Status>('/api/profile/microsoft/status');
      setStatus(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load Teams license status.');
    }
  }

  useEffect(() => {
    void load();
    const params = new URLSearchParams(window.location.search);
    const result = params.get('microsoft_personal');
    if (result === 'connected') {
      setInfo('Microsoft Teams license connected.');
      params.delete('microsoft_personal');
      params.delete('msg');
      const search = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));
    } else if (result === 'failed') {
      setError(`Microsoft Teams connection failed: ${params.get('msg') ?? 'unknown error'}`);
      params.delete('microsoft_personal');
      params.delete('msg');
      const search = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));
    }
  }, []);

  function connect() {
    window.location.href = '/api/profile/microsoft/connect';
  }

  async function disconnect() {
    if (!window.confirm('Disconnect your Microsoft Teams license?')) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/profile/microsoft/disconnect', { method: 'POST' });
      setInfo('Microsoft Teams disconnected.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to disconnect.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">My Teams license</h2>
          <p className="text-xs text-[var(--color-muted)]">
            Connect your own Microsoft 365 / Teams account. Independent of the team's Teams account
            (set up by an admin under Admin → Team Microsoft / Teams). Stored for future per-agent
            features.
          </p>
        </div>
        {status?.connected ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={connect}>
              Reconnect
            </Button>
            <Button variant="outline" onClick={() => void disconnect()} disabled={busy}>
              Disconnect
            </Button>
          </div>
        ) : (
          status?.oauthClientConfigured && <Button onClick={connect}>Connect Microsoft</Button>
        )}
      </div>

      {info && <p className="mt-3 text-sm text-green-700">{info}</p>}
      {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

      {!status ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
      ) : !status.oauthClientConfigured ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">
          Microsoft OAuth client isn't configured on the server. Ask an admin to set the
          <code className="mx-1">MICROSOFT_*</code> env vars.
        </p>
      ) : status.connected ? (
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          Connected as <strong>{status.emailAddress}</strong>
          {status.connectedAt && ` · since ${new Date(status.connectedAt).toLocaleDateString()}`}
        </p>
      ) : (
        <p className="mt-3 text-sm text-[var(--color-muted)]">
          Not connected yet. Connecting opens a Microsoft sign-in for <strong>your</strong> account
          (not the team's). Your account needs a Teams license.
        </p>
      )}
    </Card>
  );
}
